/**
 * React Query hooks for the We Met v2 bidirectional connection lifecycle.
 *
 * Replaces the legacy `useIMet` hook and supplements `useWeMetFanout`
 * (which handles the initial request creation via the drawer).
 *
 * TECH-295
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  listWeMetConnectionsV2,
  confirmWeMetConnectionV2,
  declineWeMetConnectionV2,
  deleteWeMetConnectionV2,
} from '@api-client/api-client';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { weMetConnectionsKey } from '@lib/cache/questions';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { toast } from '@hooks/use-toast';
import { WeMetConnectionStatus, deriveWeMetUIState } from '@models/api/we-met-connection';
import type { WeMetConnectionRead, WeMetUIConnectionState } from '@models/api/we-met-connection';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

// ── List query ──────────────────────────────────────────────────────────────

/**
 * Fetch all non-deleted We Met connections for the current user.
 *
 * Deliberately unfiltered: every consumer reads this one unparameterized key and
 * every mutation optimistically updates it. A status-filtered variant would need
 * its own cache-coherence story (the mutations only touch this key), so add it
 * there if/when a caller actually needs it — see `weMetConnectionsKey`.
 */
export function useWeMetConnectionsList(
  options?: Omit<
    UseQueryOptions<WeMetConnectionRead[], Error, WeMetConnectionRead[]>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: weMetConnectionsKey(),
    queryFn: async () => {
      const response = await listWeMetConnectionsV2();
      return response.data;
    },
    ...options,
  });
}

// ── Derived selectors ───────────────────────────────────────────────────────

export interface WeMetConnectionWithState {
  connection: WeMetConnectionRead | undefined;
  uiState: WeMetUIConnectionState;
}

/**
 * Pure selector: the connection row between the viewer and `otherUserId`, plus
 * its derived UI state. The single derivation behind `useWeMetConnectionWith`
 * (per-row) and batch surfaces that resolve many peers from one list read
 * (`WeMetRosterDrawer` routing create vs confirm), so the two can't drift.
 */
export function selectWeMetConnectionWith(
  connections: WeMetConnectionRead[] | undefined,
  otherUserId: string | undefined,
): WeMetConnectionWithState {
  // Defensive: skip deleted rows. The backend already excludes them from the
  // default list and a UniqueConstraint guarantees one row per peer, but the
  // type permits `deleted`, so guard here too rather than rely on call order.
  const connection =
    otherUserId && connections
      ? connections.find(
          c => c.connected_user_id === otherUserId && c.status !== WeMetConnectionStatus.DELETED,
        )
      : undefined;
  return {
    connection,
    uiState: connection ? deriveWeMetUIState(connection.status, connection.is_initiator) : 'none',
  };
}

/**
 * Find the connection row between the current user and a specific other user.
 * Used on `OtherUserProfileScreen` to determine ActionRow state.
 */
export function useWeMetConnectionWith(otherUserId: string | undefined) {
  const { data: connections, ...rest } = useWeMetConnectionsList();

  const { connection, uiState } = useMemo(
    () => selectWeMetConnectionWith(connections, otherUserId),
    [connections, otherUserId],
  );

  return { connection, uiState, ...rest };
}

/**
 * Count pending incoming requests (not_confirmed + not initiator).
 * Used for the badge count on the Connections tab.
 */
export function useWeMetPendingIncomingCount() {
  const { data: connections } = useWeMetConnectionsList();

  return useMemo(() => {
    if (!connections) return 0;
    return connections.filter(
      c => c.status === WeMetConnectionStatus.NOT_CONFIRMED && !c.is_initiator,
    ).length;
  }, [connections]);
}

// ── Mutations ───────────────────────────────────────────────────────────────

/**
 * Confirm a pending incoming connection request.
 * Only the non-initiator (recipient) can confirm.
 */
export function useConfirmWeMetConnection() {
  const queryClient = useQueryClient();
  const { trackEvent } = useAmplitudeTracking();

  return useMutation({
    mutationFn: (connectionId: string) => confirmWeMetConnectionV2(connectionId).then(r => r.data),

    onMutate: async connectionId => {
      await queryClient.cancelQueries({ queryKey: weMetConnectionsKey() });

      const previousData = queryClient.getQueryData<WeMetConnectionRead[]>(weMetConnectionsKey());

      // Optimistic: flip the connection to confirmed
      queryClient.setQueryData<WeMetConnectionRead[]>(weMetConnectionsKey(), old => {
        if (!old) return old;
        return old.map(c =>
          c.id === connectionId
            ? {
                ...c,
                status: WeMetConnectionStatus.CONFIRMED,
                confirmed_at: new Date().toISOString(),
              }
            : c,
        );
      });

      return { previousData };
    },

    onSuccess: (serverRow, connectionId) => {
      // Reconcile with server truth (step 3 of the optimistic pattern). onMutate
      // synthesized confirmed_at client-side; the confirm endpoint returns the
      // authoritative, enriched row (counterpart `user` + venue name — see
      // backend `_enrich_connection`), so swap it in directly. No client-side
      // fall-back merge: removed in #920 because backend #537 enriches every v2
      // mutation response; re-adding one would resurrect the blank-card ambiguity
      // #921 killed.
      queryClient.setQueryData<WeMetConnectionRead[]>(weMetConnectionsKey(), old =>
        old?.map(c => (c.id === connectionId ? serverRow : c)),
      );
      trackEvent(ANALYTICS_EVENTS.we_met_request_confirmed, {
        connection_id: connectionId,
      });
      toast({ title: 'Connection confirmed!' });
    },

    onError: (error, _connectionId, context) => {
      logger.error('useConfirmWeMetConnection: failed', { error: sanitizeError(error) });
      // The lifecycle hooks (confirm/decline/delete) only fire on a row the user
      // is already viewing, so the list cache is always seeded — a plain restore
      // is enough. They intentionally omit useWeMetFanout's removeQueries-on-
      // unseeded branch, which exists only because fanout can fire from a cold row.
      if (context?.previousData) {
        queryClient.setQueryData(weMetConnectionsKey(), context.previousData);
      }
      toast({
        title: 'Could not confirm connection',
        description: 'Try again in a moment.',
        variant: 'destructive',
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: weMetConnectionsKey() });
    },
  });
}

/**
 * Decline a pending incoming connection request.
 * Only the non-initiator (recipient) can decline.
 */
export function useDeclineWeMetConnection() {
  const queryClient = useQueryClient();
  const { trackEvent } = useAmplitudeTracking();

  return useMutation({
    mutationFn: (connectionId: string) => declineWeMetConnectionV2(connectionId).then(r => r.data),

    onMutate: async connectionId => {
      await queryClient.cancelQueries({ queryKey: weMetConnectionsKey() });

      const previousData = queryClient.getQueryData<WeMetConnectionRead[]>(weMetConnectionsKey());

      // Optimistic: remove the declined connection from the list
      queryClient.setQueryData<WeMetConnectionRead[]>(weMetConnectionsKey(), old => {
        if (!old) return old;
        return old.filter(c => c.id !== connectionId);
      });

      return { previousData };
    },

    onSuccess: (_data, connectionId) => {
      // No cache reconcile here (unlike confirm / create): onMutate already REMOVED
      // the row, and a declined connection has nothing left to render — so the
      // enriched server row is intentionally discarded (`_data`). Note: the dev-only
      // v2 enrichment tripwire still runs in the api-client tapper for this endpoint.
      trackEvent(ANALYTICS_EVENTS.we_met_request_declined, {
        connection_id: connectionId,
      });
    },

    onError: (error, _connectionId, context) => {
      logger.error('useDeclineWeMetConnection: failed', { error: sanitizeError(error) });
      if (context?.previousData) {
        queryClient.setQueryData(weMetConnectionsKey(), context.previousData);
      }
      toast({
        title: 'Could not decline connection',
        description: 'Try again in a moment.',
        variant: 'destructive',
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: weMetConnectionsKey() });
    },
  });
}

/**
 * Delete (remove) a We Met connection. Either party can delete.
 * - Pending outgoing → cancels the request
 * - Confirmed → removes the connection
 */
export function useDeleteWeMetConnection() {
  const queryClient = useQueryClient();
  const { trackEvent } = useAmplitudeTracking();

  return useMutation({
    mutationFn: (connectionId: string) => deleteWeMetConnectionV2(connectionId).then(r => r.data),

    onMutate: async connectionId => {
      await queryClient.cancelQueries({ queryKey: weMetConnectionsKey() });

      const previousData = queryClient.getQueryData<WeMetConnectionRead[]>(weMetConnectionsKey());

      // Optimistic: remove the connection from the list
      queryClient.setQueryData<WeMetConnectionRead[]>(weMetConnectionsKey(), old => {
        if (!old) return old;
        return old.filter(c => c.id !== connectionId);
      });

      return { previousData };
    },

    onSuccess: (_data, connectionId) => {
      trackEvent(ANALYTICS_EVENTS.we_met_connection_deleted, {
        connection_id: connectionId,
      });
      toast({ title: 'Connection removed' });
    },

    onError: (error, _connectionId, context) => {
      logger.error('useDeleteWeMetConnection: failed', { error: sanitizeError(error) });
      if (context?.previousData) {
        queryClient.setQueryData(weMetConnectionsKey(), context.previousData);
      }
      toast({
        title: 'Could not remove connection',
        description: 'Try again in a moment.',
        variant: 'destructive',
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: weMetConnectionsKey() });
    },
  });
}
